import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { tokensService } from '@/features/payment';
import type { TokenPackage } from '@/types/tokens';

const TOKEN_PACKAGES_KEY = 'token-packages';

/**
 * Token-package catalog (GET /v1/tokens/packages) — the small "cookie" packs
 * sold to everyone. Copy + localized price are server-driven and
 * locale-sensitive, so the locale is part of the cache key (mirrors
 * `usePasses` / `useStoreCatalog`). Cached + persisted like the sibling
 * catalogs so the packs are instant on remount instead of re-skeletoning.
 *
 * `enabled` gates the fetch — the token cards mount inside root-level paywall
 * dialogs, so callers can defer the auth-only request until it's about to show.
 */
export function useTokenPackages({ enabled = true }: { enabled?: boolean } = {}) {
  const locale = useLocale();
  return useQuery<TokenPackage[]>({
    queryKey: [TOKEN_PACKAGES_KEY, locale],
    queryFn: async () => {
      const response = await tokensService.getPackages();
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min — pack copy/price is stable
    gcTime: 30 * 60 * 1000,
  });
}
